<script setup lang="ts">
import registerUser from "@/assets/imgs/register_user.png";
import { GenderTypes } from "@/enums/genderTypes";
import NonAuthenticatedLayout from "@/layouts/NonAuthenticatedLayout.vue";
import useAccount from "@/composables/useAccount";
import router from "@/router";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { ref } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";

const toast = useToast();
const route = useRoute();

const form = ref({
  name: "",
  secondName: "",
  lastName: "",
  secondLastName: "",
  geneder: GenderTypes.Neutral,
  email: "",
});

const rules = {
  name: {
    required: helpers.withMessage("El nombre es requerido", required),
  },
  lastName: {
    required: helpers.withMessage("El apellido es requerido", required),
  },
  geneder: {
    required: helpers.withMessage("El género es requerido", required),
  },
  email: {
    required: helpers.withMessage("El correo es requerido", required),
    requiredIf: helpers.withMessage(
      "El correo debe ser válido",
      (value: string) => {
        const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
        return regex.test(value);
      },
    ),
  },
};

const formVuelidate = useVuelidate(rules, form);

const message = ref("");
const isFailed = ref(false);
const isSubmitting = ref(false);

const { registerUserInvitationAsync } = useAccount();

const genderOptions = Object.values(GenderTypes).map((g) => ({ label: g, value: g }));

const clearQuery = () => {
  const query = Object.assign({}, route.query);
  delete query.company;
  delete query.expires;
  delete query.signature;
  router.push({ query });
};

const handleSubmit = async () => {
  formVuelidate.value.$touch();
  if (formVuelidate.value.$invalid) {
    toast.error("Por favor, corrija los errores del formulario");
    return;
  }

  isSubmitting.value = true;
  try {
    await registerUserInvitationAsync({
      params: {
        name: form.value.name,
        second_name: form.value.secondName,
        lastname: form.value.lastName,
        second_lastname: form.value.secondLastName,
        gender: form.value.geneder,
        email: form.value.email,
        company: route.query.company as string,
        expires: route.query.expires as string,
        signature: route.query.signature as string,
      },
      config: { raiseToast: false },
    });

    clearQuery();
    message.value =
      "Su usuario ha sido registrado correctamente. Será necesaria la aprobación de un administrador para acceder al sistema.";
    isFailed.value = false;
    toast.success("Registro con éxito");
  } catch (err: unknown) {
    const error = err as { response?: { data?: { message?: string }; status?: number } };
    toast.error(error?.response?.data?.message);
    if (error?.response?.status === 403) {
      message.value =
        "Ha ocurrido un error al registrar su usuario. Por favor, contacte con su administrador para resolver el problema.";
      isFailed.value = true;
      clearQuery();
    }
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <NonAuthenticatedLayout>
    <!-- Full navy background -->
    <div class="min-h-[calc(100vh-136px)] bg-[#021F58] relative overflow-hidden flex items-center justify-center px-4 py-10">
      <!-- Decorative blobs -->
      <div class="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/5 pointer-events-none"></div>
      <div class="absolute bottom-0 -left-20 w-80 h-80 rounded-full bg-white/5 pointer-events-none"></div>
      <div class="absolute top-1/2 left-1/4 w-40 h-40 rounded-full bg-white/3 pointer-events-none"></div>

      <!-- Card -->
      <div class="relative z-10 w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden">

        <!-- Card header -->
        <div class="flex flex-col sm:flex-row">
          <!-- Left accent: illustration -->
          <div class="hidden sm:flex flex-col justify-center items-center bg-[#021F58]/5 w-52 flex-shrink-0 px-6 py-8 gap-4 border-r border-gray-100">
            <img
              :src="registerUser"
              alt="Registro de usuario"
              class="w-36"
              style="mix-blend-mode: multiply;"
            />
            <div class="text-center">
              <p class="text-[10px] font-bold text-[#021F58] uppercase tracking-widest">Plataforma</p>
              <p class="text-xs text-gray-500 mt-0.5 leading-tight">Gestión de viajes<br/>corporativos</p>
            </div>
          </div>

          <!-- Right: form area -->
          <div class="flex-1 px-7 py-8">
            <template v-if="!message">
              <div class="mb-6">
                <div class="flex items-center gap-2 mb-3">
                  <div class="w-7 h-7 bg-[#021F58] rounded-lg flex items-center justify-center">
                    <span class="material-symbols-outlined text-white text-sm">how_to_reg</span>
                  </div>
                  <span class="text-xs font-bold text-[#021F58] uppercase tracking-widest">Nuevo usuario</span>
                </div>
                <h1 class="text-2xl font-bold text-gray-900 leading-tight">Registro de usuario</h1>
                <p class="text-sm text-gray-400 mt-1">Completa tu información para activar tu cuenta.</p>
              </div>

              <form @submit.prevent="handleSubmit" class="flex flex-col gap-4">
                <!-- Nombre + Segundo nombre -->
                <div class="grid grid-cols-2 gap-3">
                  <div class="flex flex-col gap-1">
                    <label class="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                      Nombre <span class="text-red-500">*</span>
                    </label>
                    <input
                      v-model="form.name"
                      type="text"
                      placeholder="Ej. Juan"
                      class="w-full text-sm rounded-xl px-3 py-2.5 border focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 focus:border-[#021F58] placeholder-gray-300 transition-all"
                      :class="formVuelidate.name.$error ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'"
                    />
                    <p v-if="formVuelidate.name.$error" class="text-xs text-red-500 flex items-center gap-1">
                      <span class="material-symbols-outlined text-xs leading-none">error</span>
                      {{ formVuelidate.name.$errors[0]?.$message }}
                    </p>
                  </div>

                  <div class="flex flex-col gap-1">
                    <label class="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Segundo nombre</label>
                    <input
                      v-model="form.secondName"
                      type="text"
                      placeholder="Opcional"
                      class="w-full text-sm rounded-xl px-3 py-2.5 border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 focus:border-[#021F58] placeholder-gray-300 transition-all"
                    />
                  </div>
                </div>

                <!-- Apellido + Segundo apellido -->
                <div class="grid grid-cols-2 gap-3">
                  <div class="flex flex-col gap-1">
                    <label class="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                      Apellido <span class="text-red-500">*</span>
                    </label>
                    <input
                      v-model="form.lastName"
                      type="text"
                      placeholder="Ej. García"
                      class="w-full text-sm rounded-xl px-3 py-2.5 border focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 focus:border-[#021F58] placeholder-gray-300 transition-all"
                      :class="formVuelidate.lastName.$error ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'"
                    />
                    <p v-if="formVuelidate.lastName.$error" class="text-xs text-red-500 flex items-center gap-1">
                      <span class="material-symbols-outlined text-xs leading-none">error</span>
                      {{ formVuelidate.lastName.$errors[0]?.$message }}
                    </p>
                  </div>

                  <div class="flex flex-col gap-1">
                    <label class="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Segundo apellido</label>
                    <input
                      v-model="form.secondLastName"
                      type="text"
                      placeholder="Opcional"
                      class="w-full text-sm rounded-xl px-3 py-2.5 border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 focus:border-[#021F58] placeholder-gray-300 transition-all"
                    />
                  </div>
                </div>

                <!-- Género + Correo en una fila -->
                <div class="grid grid-cols-2 gap-3">
                  <div class="flex flex-col gap-1">
                    <label class="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                      Género <span class="text-red-500">*</span>
                    </label>
                    <div class="relative">
                      <select
                        v-model="form.geneder"
                        class="w-full text-sm rounded-xl px-3 py-2.5 border focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 focus:border-[#021F58] transition-all appearance-none pr-8"
                        :class="formVuelidate.geneder.$error ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'"
                      >
                        <option v-for="opt in genderOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                      </select>
                      <span class="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
                        <span class="material-symbols-outlined text-gray-400 text-base">expand_more</span>
                      </span>
                    </div>
                  </div>

                  <div class="flex flex-col gap-1">
                    <label class="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                      Correo <span class="text-red-500">*</span>
                    </label>
                    <div class="relative">
                      <span class="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <span class="material-symbols-outlined text-gray-400 text-[16px]">mail</span>
                      </span>
                      <input
                        v-model="form.email"
                        type="email"
                        placeholder="usuario@empresa.com"
                        class="w-full text-sm rounded-xl pl-9 pr-3 py-2.5 border focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 focus:border-[#021F58] placeholder-gray-300 transition-all"
                        :class="formVuelidate.email.$error ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'"
                      />
                    </div>
                    <p v-if="formVuelidate.email.$error" class="text-xs text-red-500 flex items-center gap-1">
                      <span class="material-symbols-outlined text-xs leading-none">error</span>
                      {{ formVuelidate.email.$errors[0]?.$message }}
                    </p>
                  </div>
                </div>

                <!-- Submit -->
                <button
                  type="submit"
                  :disabled="isSubmitting"
                  class="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#021F58] text-white text-sm font-semibold hover:bg-[#1C3F7A] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-2 shadow-sm"
                >
                  <div v-if="isSubmitting" class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span v-else class="material-symbols-outlined text-[18px]">how_to_reg</span>
                  {{ isSubmitting ? 'Registrando...' : 'Crear cuenta' }}
                </button>

                <p class="text-center text-xs text-gray-400 flex items-center justify-center gap-1">
                  <span class="material-symbols-outlined text-xs">lock</span>
                  Tu información está protegida y encriptada
                </p>
              </form>
            </template>

            <!-- Success / error state -->
            <template v-else>
              <div class="flex flex-col items-center text-center gap-5 py-6">
                <div
                  class="w-16 h-16 rounded-2xl flex items-center justify-center"
                  :class="isFailed ? 'bg-red-100' : 'bg-[#021F58]/10'"
                >
                  <span
                    class="material-symbols-outlined text-3xl"
                    :class="isFailed ? 'text-red-500' : 'text-[#021F58]'"
                  >{{ isFailed ? 'cancel' : 'check_circle' }}</span>
                </div>
                <div>
                  <h2 class="text-lg font-bold mb-1.5" :class="isFailed ? 'text-red-700' : 'text-gray-900'">
                    {{ isFailed ? 'Error en el registro' : '¡Registro exitoso!' }}
                  </h2>
                  <p class="text-sm text-gray-500 leading-relaxed max-w-xs">{{ message }}</p>
                </div>
                <div v-if="!isFailed" class="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
                  <span class="material-symbols-outlined text-base">hourglass_top</span>
                  Pendiente de aprobación del administrador
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </NonAuthenticatedLayout>
</template>
